import * as React from 'react';
import type {FormAdapter, FormFieldValue} from '../useFormRegister';

const isReactInputEvent = (
  event: React.ChangeEvent<HTMLElement> | FormFieldValue,
): event is React.ChangeEvent<HTMLInputElement> => {
  return (
    !!event &&
    typeof event === 'object' &&
    'target' in event &&
    'checked' in event.target
  );
};

/**
 * A hook that provides adapter functions for Radio form integration.
 *
 * Radio buttons are mutually exclusive within a group and use the `value` prop
 * to determine what value to set in the form when checked.
 */
export function useRadioFormAdapter(): FormAdapter<FormFieldValue, boolean> {
  const valueAdapter = React.useCallback<
    FormAdapter<FormFieldValue, boolean>['valueAdapter']
  >((fieldValue, controlValue) => {
    // A radio button is checked if the field value equals the radio's value
    return fieldValue === controlValue;
  }, []);

  const changeAdapter = React.useCallback<
    FormAdapter<FormFieldValue, boolean>['changeAdapter']
  >((event, fieldValue, controlValue) => {
    // If it's not a React event, return the field value unchanged
    if (!isReactInputEvent(event)) {
      return fieldValue;
    }

    // When a radio button is checked, set the form field to the radio's value
    // When unchecked, we typically don't change the form value since radio buttons
    // are mutually exclusive and another radio in the group should be selected
    if (event.target.checked) {
      return controlValue;
    }

    // For radio buttons, we generally don't handle unchecking explicitly
    // since they are mutually exclusive - another radio should be selected
    return fieldValue;
  }, []);

  return {
    valueAdapter,
    changeAdapter,
  };
}
